import React, { Component, Fragment } from "react";
import {
    groupProcessorItems,
    ProcessorItem
} from "../../models/MailroomProcessorItem";
import * as _ from "lodash";
import {
    TextField,
    IDropdownOption,
    ComboBox,
    IComboBoxOption,
    DropdownMenuItemType,
    Toggle
} from "office-ui-fabric-react";
import { debounce } from "throttle-debounce";
import ProcessorItemDetailCard from "./ProcessorItemDetailCard";
import ProcessorItemTable from "./ProcessorItemTable";


const STATUS_DROPDOWN_OPTIONS: IDropdownOption[] = [
    {
        text: "All",
        key: "all"
    },
    {
        text: "Success",
        key: "success"
    },
    {
        text: "Error",
        key: "error"
    }
];

interface IProcessorItemPropertyFilter
{
    type: "mis" | "sds",
    propertyName?: string;
    isAll: boolean;
}

interface IProcessorItemPropertyDropdownOption extends IDropdownOption
{
    data?: IProcessorItemPropertyFilter
}

export interface ProcessorItemsViewerProps {
    title?: string;
    processorItems: ProcessorItem[] | undefined;
    
    minHeightForItems?: string;
}

export interface ProcessorItemsViewerState {
    selectedItem: ProcessorItem | undefined;
    relatedItems: ProcessorItem[];
    filterString?: string;
    filterCaseSensitive: boolean;
    filteredProcessorItems: ProcessorItem[] | undefined;
    filterTypeOptions: IDropdownOption[];
    selectedFilterTypeOptions: IDropdownOption[];
    selectedFilterStatus: "all" | "success" | "error", 
    
    filterPropertyOptions: IProcessorItemPropertyDropdownOption[];
    selectedFilterPropertyOptions: IProcessorItemPropertyDropdownOption[];
}

export default class ProcessorItemsViewer extends Component<ProcessorItemsViewerProps, ProcessorItemsViewerState> {

    private readonly debouncedFilterAndSetItems: debounce<() => void>;

    constructor(props: Readonly<ProcessorItemsViewerProps>) {
        super(props);

        this.debouncedFilterAndSetItems = debounce(200, this.filterAndSetItems);

        this.state = {
            selectedItem: undefined,
            relatedItems: [],
            filterString: undefined,
            filterCaseSensitive: false,
            filteredProcessorItems: undefined,
            filterTypeOptions: [],
            selectedFilterTypeOptions: [],
            selectedFilterStatus: "all",
            filterPropertyOptions: [],
            selectedFilterPropertyOptions: []
        }
    }

    public componentDidMount() {
        this.filterAndSetItems();
        this.setFilterTypeOptions();
        this.setFilterPropertyOptions();
    }

    componentDidUpdate(prevProps: Readonly<ProcessorItemsViewerProps>, prevState: Readonly<ProcessorItemsViewerState>, snapshot?: any): void {
        if (this.props.processorItems !== prevProps.processorItems || this.state.filterString !== prevState.filterString || this.state.selectedFilterTypeOptions !== prevState.selectedFilterTypeOptions || this.state.selectedFilterPropertyOptions !== prevState.selectedFilterPropertyOptions || this.state.selectedFilterStatus !== prevState.selectedFilterStatus || this.state.filterCaseSensitive !== prevState.filterCaseSensitive) {
            if (prevProps.processorItems === undefined) {
                this.setFilterTypeOptions();
                this.setFilterPropertyOptions();
            }
            this.debouncedFilterAndSetItems();
        }
    }

    private setFilterTypeOptions() {
        if (!this.props.processorItems) {
            return;
        }

        const groupedProcessorItemsResult = groupProcessorItems(this.props.processorItems!);
        const filterTypeOptions = _.keys(groupedProcessorItemsResult.typesGroupedByShortType).map((shortType): IDropdownOption => {
            return {
                text: shortType,
                key: shortType,
                data: groupedProcessorItemsResult.typesGroupedByShortType[shortType]
            };
        });

        this.setState({
            filterTypeOptions
        });
    }
    
    private setFilterPropertyOptions()
    {
        if (!this.props.processorItems) {
            return;
        }
        
        const allMisOption: IProcessorItemPropertyDropdownOption = {text: "All Mis", key: "allMis", data: {propertyName: undefined,isAll: true,type: "mis"}};
        const misHeader: IProcessorItemPropertyDropdownOption = {text: "Mis", key: "Mis", data: undefined, itemType: DropdownMenuItemType.Header };
        const sdsHeader: IProcessorItemPropertyDropdownOption = {text: "Sds", key: "Sds", data: undefined, itemType: DropdownMenuItemType.Header };
    
        const allSdsOption: IProcessorItemPropertyDropdownOption = {text: "All Sds", key: "allSds", data: {propertyName: undefined,isAll: true,type: "sds"}};

        
        const buildOptions = (processorItems: ProcessorItem[], objectSelector: (pi: ProcessorItem) =>  any,type: "mis" | "sds"): IProcessorItemPropertyDropdownOption[] => {

            const objects = processorItems.map(pi => objectSelector(pi)).filter(o => o !== null);
            const objectsGroupedByType = _.groupBy(objects, o => o["$type"]);

            var propertyNames = Object.keys(objectsGroupedByType).flatMap(type => {
                const first = objectsGroupedByType[type][0];
                return Object.keys(first);
            });
            
            return _.uniq(propertyNames).map(propertyName =>{
                return {text: propertyName, key: type + ":" + propertyName, data: {propertyName: propertyName, type: type,isAll: false}}
            });
        };

        console.time("buildPropertyOptions");

        const allMisPropertyOptions = buildOptions(this.props.processorItems,pi => pi.misItem,"mis");
        const allSdsPropertyOptions = buildOptions(this.props.processorItems,pi => pi.sdsRow,"sds");

        const filterPropertyOptions = _.uniq([allMisOption,allSdsOption,misHeader,...allMisPropertyOptions,sdsHeader, ...allSdsPropertyOptions]);

        console.timeEnd("buildPropertyOptions");

        this.setState({
            filterPropertyOptions
        });
    }

    private searchObject(object: any, searchTerm: string,keys: string[] | undefined,caseSensitive: boolean): boolean {

        if(!caseSensitive)
        {
            searchTerm = searchTerm.toLowerCase();
        }
        
        if (!object) {
            return false;
        }

        // Get key value paais of all properties and nested properties
        const flattenValues = (data: object): any => _.flatMap(data, (value, key) => (_.isObject(value) ? flattenValues(value) : { key, value }));
        const kvps = flattenValues(object);

        const values = keys === undefined ?
            _.map(kvps, kvp => kvp.value) : // If no keys specfied then map all values
            _.flatMap(keys, key => _.map(_.filter(kvps, kvp => kvp.key === key), kvp => kvp.value)); // Lookup all kvps that match each key then map to value

        return _.some(values, (v) => {
            if (!v) {
                return false;
            }

            if (typeof v === "string") {
                if (!caseSensitive) {
                    v = v.toLowerCase();
                }
                return v.indexOf(searchTerm) !== -1;
            }

            return false;
        });
    }

    private filterAndSetItems() {
        if (!this.props.processorItems) {
            this.setState({
                filteredProcessorItems: undefined
            });

            return;
        }

        let filteredProcessorItems = this.props.processorItems!;

        console.time("filterProcessorItems");

        if (this.state.selectedFilterTypeOptions.length >= 1) {

            const types = _.flatten(this.state.selectedFilterTypeOptions.map(o => o.data));
            filteredProcessorItems = filteredProcessorItems.filter((pi: ProcessorItem) => {
                return _.includes(types, pi.$type);
            });
        }

        if (this.state.filterString) {
            
            let misPropertyNames: string[] | undefined = undefined;
            let sdsPropertyNames: string[] | undefined = undefined;
            
            if(this.state.selectedFilterPropertyOptions.length > 0)
            {
                misPropertyNames = this.state.selectedFilterPropertyOptions.filter(o => o.data?.type === "mis").map(o => o.data?.propertyName!);
                sdsPropertyNames = this.state.selectedFilterPropertyOptions.filter(o => o.data?.type === "sds").map(o => o.data?.propertyName!);
            }

            const searchTerm = this.state.filterString;

            filteredProcessorItems = filteredProcessorItems.filter((pi: ProcessorItem) => {
                return this.searchObject(pi.misItem, searchTerm,misPropertyNames,this.state.filterCaseSensitive) || this.searchObject(pi.sdsRow, searchTerm,sdsPropertyNames,this.state.filterCaseSensitive);
            });
        }
        
        
        switch (this.state.selectedFilterStatus)
        {
            case "success":
                filteredProcessorItems = filteredProcessorItems.filter(pi => {
                    return pi.error === false;
                });
                break;
            case "error":
                filteredProcessorItems = filteredProcessorItems.filter(pi => {
                    return pi.error === true;
                });
                break;
            case "all":
            default:
                break;
        }
        
        console.timeEnd("filterProcessorItems");

        this.setState({
            filteredProcessorItems
        });
    }

    private onItemSelected = (selectedItem: ProcessorItem) => {

        let fromSelectedIds = _.values(selectedItem).filter(v => _.has(v, "$ref")).map(v => v["$ref"]);
        const fromSelectedItems = this.props.processorItems!.filter(item => _.includes(fromSelectedIds, item["$id"]))

        const toSelectedItems = this.props.processorItems!.filter(item => {
            const itemRefIds = _.values(item).filter(v => _.has(v, "$ref")).map(v => v["$ref"]);
            return _.includes(itemRefIds, selectedItem["$id"]);
        })

        const relatedItems = [...fromSelectedItems, ...toSelectedItems];

        this.setState({
            selectedItem: selectedItem,
            relatedItems
        });

        //make sure the selected item is visible in the main processor items table
        const itemIsInCurrentFilteredItems = _.includes(this.state.filteredProcessorItems, selectedItem);
        if (!itemIsInCurrentFilteredItems) {
            this.setState({
                filterString: ""
            });
            this.filterAndSetItems();
        }
    }

    private onFilterTypeSelected = (event: any, option?: IComboBoxOption) => {

        if (!option) {
            return;
        }

        const newSelectedOptions = [...this.state.selectedFilterTypeOptions];
        if (option.selected) {
            newSelectedOptions.push(option);
        } else {
            _.remove(newSelectedOptions, (existingOption) => existingOption.key === option.key);
        }

        this.setState({
            selectedFilterTypeOptions: newSelectedOptions
        });
    }

    private onFilterPropertySelected = (event: any, option?: IProcessorItemPropertyDropdownOption) => {
        if(!option)
        {
            return;
        }
        
        if(option.data?.isAll)
        {
            const relatedOptions = this.state.filterPropertyOptions.filter(o => o.data?.type === option.data!.type);

            if(option.selected)
            {
                const newSelectedOptions = [option,...this.state.selectedFilterPropertyOptions,...relatedOptions];
                
                this.setState({
                    selectedFilterPropertyOptions: _.uniqBy(newSelectedOptions,o => o.key)
                });
            }
            else
            {
                const newSelectedOptions = [...this.state.selectedFilterPropertyOptions];
                
                _.remove(newSelectedOptions,o => o.key === option.key);
                
                const relatedKeys = relatedOptions.map(o => o.key);
                _.remove(newSelectedOptions,o => relatedKeys.includes(o.key))
                
                this.setState({
                    selectedFilterPropertyOptions: newSelectedOptions
                });
            }
        }
        else
        {
            const newSelectedOptions = [...this.state.selectedFilterPropertyOptions];
            _.remove(newSelectedOptions,o => o.data?.isAll === true && o.data.type === option.data!.type);
            
            if(option.selected)
            {
                newSelectedOptions.push(option);
            }
            else
            {
                _.remove(newSelectedOptions,o => o.key === option.key);
            }
            
            this.setState({
                selectedFilterPropertyOptions: newSelectedOptions
            });
        }
        
    }
    
    public render() {

        let tableTitle = "Processor Items";
        if (this.props.title) {
            tableTitle = this.props.title;
        }
        else {
            if (this.props.processorItems) {
                tableTitle = `Showing ${this.state.filteredProcessorItems?.length} of ${this.props.processorItems.length} ${tableTitle}`;
            }
        }
        
        const styleForItems: React.CSSProperties = {};
        if(this.props.minHeightForItems)
        {
            styleForItems.minHeight = this.props.minHeightForItems;
        }


        return (
            <Fragment>

                <div className="filter-controls p-top-1rem">
                    <div className="filter-controls__row">
                        <TextField className="filter-controls__input" autoFocus label="Search items" placeholder="Filter" value={this.state.filterString} onChange={
                            (event, newValue) => {
                                this.setState({
                                    filterString: newValue
                                });
                            }
                        } />

                        <Toggle
                            className={"filter-controls__input flex--align-self-flex-end"}
                            inlineLabel={true}
                            checked={this.state.filterCaseSensitive}
                            onChange={(event, newValue) => {
                                this.setState({
                                    filterCaseSensitive: newValue!
                                });
                            }}
                            role="switch"
                            label={"Case sensitive"}
                        />
                        
                        <ComboBox
                            className="filter-controls__input"
                            multiSelect
                            selectedKey={this.state.selectedFilterPropertyOptions.map(o => o.key as string)}
                            label={"Field"}
                            allowFreeform={false}
                            autoComplete={"on"}
                            options={this.state.filterPropertyOptions}
                            onChange={this.onFilterPropertySelected}
                            text={this.state.selectedFilterPropertyOptions.length === 0 ? "All" : undefined}
                        />
                        
                        <ComboBox
                            className="filter-controls__input"
                            multiSelect
                            selectedKey={this.state.selectedFilterTypeOptions.map(o => o.key as string)}
                            label={"Type"}
                            allowFreeform={false}
                            options={this.state.filterTypeOptions}
                            onChange={this.onFilterTypeSelected}
                            text={""}
                        />
                        <ComboBox
                            className="filter-controls__input"
                            selectedKey={this.state.selectedFilterStatus}
                            label={"Status"}
                            allowFreeform={false}
                            options={STATUS_DROPDOWN_OPTIONS}
                            onChange={(e,v) => {
                                this.setState({
                                    selectedFilterStatus: v?.key as any
                                });
                            }}
                            text={""}
                        />

             
                    </div>
                </div>
                
                <div style={styleForItems} className={"flex-row flex-item--flex-1"}>

                    <ProcessorItemTable title={tableTitle} className={"flex-item--flex-1 cdb-table__min-height-500"} allItems={this.props.processorItems} shownItems={this.state.filteredProcessorItems} selectedItem={this.state.selectedItem} virtualize={true} onItemClicked={this.onItemSelected}>
                        {this.props.children}
                    </ProcessorItemTable>

                    <div className={"flex-item--flex-1"}>
                        {this.state.selectedItem != undefined ? <ProcessorItemDetailCard item={this.state.selectedItem} allItems={this.props.processorItems!} relatedItems={this.state.relatedItems} onRelatedItemSelected={this.onItemSelected} /> : null}
                    </div>
                </div>

            </Fragment>
        )
    };
}